// Starts the Precon app: signs the person in with their Westbuilt Microsoft account through
// Supabase, then provides the storage and services the app expects and loads it.
import { createClient } from "@supabase/supabase-js";
import * as XLSX from "xlsx";
import { createStore } from "./db.js";
import { createUserService, createDownloads, createHubspot, createEstimatingService } from "./services.js";
import { withEditVersions } from "./estimating-versions.js";
import { createMicrosoft, MICROSOFT_SERVICES } from "./microsoft-session.js";

window.XLSX = XLSX; // used by template import/export; bundled so no third-party script is loaded

const cfg = window.PRECON_CONFIG || {};
const root = () => document.getElementById("app");

function screen(inner) {
  root().innerHTML = `<div class="signin-wrap"><div class="signin-card">${inner}</div></div>`;
}
function signInScreen(sb, message) {
  screen(`
    <h1>Precon</h1>
    <p>Westbuilt preconstruction workflow. Sign in with your Westbuilt Microsoft account.</p>
    ${message ? `<p class="err" style="margin:0 0 12px;">${message}</p>` : ""}
    <button type="button" class="btn primary" id="ms-signin" style="width:100%; height:40px;">Sign in with Microsoft</button>
    <p class="note">Access is managed in the Westbuilt Hub. If you can't get in, ask a Precon administrator.</p>`);
  document.getElementById("ms-signin").addEventListener("click", async () => {
    const { error } = await sb.auth.signInWithOAuth({
      provider: "azure",
      options: { scopes: "openid email profile", redirectTo: window.location.origin + window.location.pathname },
    });
    if (error) signInScreen(sb, "Sign-in couldn't start. Please try again.");
  });
}

async function start() {
  if (!cfg.supabaseUrl || !cfg.supabaseAnonKey) {
    screen(`<h1>Precon</h1><p>This deployment isn't configured yet (missing Supabase settings).</p>`);
    return;
  }
  const sb = createClient(cfg.supabaseUrl, cfg.supabaseAnonKey, {
    auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, flowType: "pkce" },
  });
  const { data: { session } } = await sb.auth.getSession();
  sb.auth.onAuthStateChange((event) => { if (event === "SIGNED_OUT") window.location.reload(); });
  if (!session) { signInScreen(sb); return; }

  // Loads one of the app's own scripts.
  const load = (src) => new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src; s.onload = resolve; s.onerror = reject;
    document.body.appendChild(s);
  });
  // SharePoint is reached with the person's own Microsoft sign-in (see microsoft-session.js).
  const microsoft = createMicrosoft(cfg, { load, email: async () => { const { data: { session: s } } = await sb.auth.getSession(); return (s && s.user && s.user.email) || ""; } });

  const services = {
    db: createStore(sb),
    user: createUserService(sb, session),
    downloads: createDownloads(),
    mcp: createHubspot(sb),
    // saves carry the version of the estimate the person opened, so two people editing the same draft can't silently overwrite each other
    estimating: withEditVersions(createEstimatingService(sb), sb),
  };
  window.PRECON_PLATFORM = {
    name: "supabase",
    hubUrl: cfg.hubUrl || "https://hub.westbuilt.homes",
    signOut: async () => { await microsoft.clear(); return sb.auth.signOut(); },
    // Microsoft sign-in for SharePoint: connect() may open a Microsoft sign-in window, so call it straight from a click.
    microsoft: { enabled: microsoft.enabled, connect: () => microsoft.token({ interactive: true }).then(() => true) },
    // Server functions the add-ons may call, with the person's session. Errors come back as { code, message }.
    async api(name, body, { raw = false } = {}) {
      if (!["pandadoc", "hubspot-activity", "sales-requests", "estimate-admin", "job-documents", "approval-forms"].includes(name)) throw { code: "bad_request", message: "Unknown service" };
      const { data: { session: s } } = await sb.auth.getSession();
      if (!s) throw { code: "sign_in", message: "Signed out" };
      const headers = { "Content-Type": "application/json", Authorization: "Bearer " + s.access_token };
      // The Microsoft token when one can be had without a sign-in window; the server says if it needs one.
      if (MICROSOFT_SERVICES.includes(name) && microsoft.enabled) {
        try { const t = await microsoft.token(); if (t) headers["X-Ms-Token"] = t; } catch (e) { /* the server answers sharepoint_sign_in */ }
      }
      let res;
      try {
        res = await fetch(`/.netlify/functions/${name}`, { method: "POST", headers, body: JSON.stringify(body || {}) });
      } catch (e) { throw { code: "offline", message: "Couldn't reach the server" }; }
      if (raw && res.ok) return res.blob();
      let data = null;
      try { data = await res.json(); } catch (e) { /* not JSON */ }
      if (!res.ok) throw { code: (data && data.code) || "server_error", message: (data && data.error) || "The request failed", detail: (data && data.detail) || "", extra: data || {} };
      return data;
    },
  };
  window.claude = { use: async (name) => services[name] || null };
  // Load the app, then its add-ons (they use the app's globals, so order matters).
  await load("/precon.js");
  await load("/hubspot-live.js");
  await load("/dashboards.js");
  await load("/workflow-extras.js");
  await load("/workflow-gantt.js");   // after workflow-extras: the Gantt option of the workflow View selector
  await load("/checklists.js");
  await load("/drafting.js");
  await load("/contracts.js");
  await load("/interactions.js");
  await load("/job-documents.js");   // after interactions: the Documents card sits below it on the job page
  await load("/job-quotes.js");      // after job documents: the quote register sits below the Documents card and uploads through it
  await load("/approval-forms.js");  // after job quotes: site details and approvals forms on the job page, form templates in Administration
  await load("/estimating.js");
  await load("/estimating-rates.js");
  await load("/estimating-components.js");
  await load("/estimating-formulas.js");
  await load("/estimating-formula-editor.js");
  await load("/estimating-contracts.js");
  await load("/estimating-lines.js");
  await load("/estimating-custom-pricelist.js");
  await load("/estimating-custom.js");
  await load("/estimating-custom-ep.js");
  await load("/estimating-custom-log.js");
  await load("/estimating-checklists.js");
  await load("/estimating-recalc.js");
  await load("/estimating-layout.js");
  await load("/estimating-archive.js");   // after the Estimating add-ons: Job Estimates tabs, archive and delete
  await load("/reports.js");   // it wraps the finished page
  await load("/sales-requests.js");   // after reports: adds its button beside Reports on the Estimating page
}
start().catch(() => screen(`<h1>Precon</h1><p>Something went wrong starting the app. Please reload the page.</p>`));
